import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import DocumentTitle from 'react-document-title';
import { toastr } from 'react-redux-toastr';
import QRCode from 'qrcode.react';
import { find, get } from 'lodash';
import IASConnectModal from './IASConnectModal';
import EmailLoginModal from './EmailLoginModal';
import RegisterModal from './RegisterModal';
import { AUTH_MODES, getAuthMode, completeSsoRedirectIfPresent, startSsoLogin } from '../utils/helpers/ssoAuth';
import {
  loadMarketplaces,
  loadPublicEvents,
  setCustomerPlatformLink,
  setTemplate
} from '../actions';
import {
  checkLink,
  getConfig,
  getTemplate,
  isJSON,
  makeAssetLink,
  makeCompanyCopyright,
  makeTemplate,
} from '../utils/helpers';
import { externalAssetNames, urlsMap } from '../utils/constants';
import { Icon } from 'semantic-ui-react';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sidebarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1 0 500px',
    height: '100vh',
    overflowY: 'auto',
    padding: 35,
    backgroundColor: '#fff',
  },
  logo: {
    display: 'block',
    maxWidth: 400,
    maxHeight: 200,
    margin: '0px auto',
  },
  backgroundContainer: {
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    flex: '3 1 75%',
    height: '100vh',
  },
  link: {
    color: '#4183c4',
  },
};

class Login extends Component {
  state = {
    iasConnectUrl: '',
    iasConnectModalOpen: false,
    emailLoginModalOpen: false,
    registerModalOpen: false,
  };

  componentDidMount() {
    toastr.clean();

    // Complete an SSO redirect-flow login if we returned here with tokens in the URL fragment.
    // On success this navigates into the app; a no-fragment load is a harmless no-op.
    completeSsoRedirectIfPresent().catch(() =>
      toastr.error('Sign-in failed', 'We couldn’t complete sign-in. Please try again.')
    );

    window.addEventListener('message', this.handleMessage);

    const mpId = getConfig('marketplaceId');
    const isPublicListingsEnabled = getConfig('enablePublicEventsAtLogin');
    if (mpId === '0') {
      this.props.navigate('/default');
    } else {
      this.loadPlatformLink();
      this.loadMarketplaceFeatures();
      if (isPublicListingsEnabled) {
        this.props.loadPublicEvents(mpId);
      }
      
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  handleMessage = e => {
    if (isJSON(e.data)) {
      const postMessage = JSON.parse(e.data);
      if (postMessage.channel === 'ias-connect') {
        if (postMessage.redirectURI) {
          window.location = postMessage.redirectURI;
        }
        this.setState({ iasConnectModalOpen: false, iasConnectUrl: '' });
      }
    }
  };

  loadGAID = marketplaceList => {
    const mpId = getConfig('marketplaceId');
    const parentMarketplace = find(
      marketplaceList,
      mp => mp.marketplaceId == mpId
    );
    const gaId = get(parentMarketplace, 'gaId') || getConfig('gaId');
    if (gaId) this.props.cookies.set('gaId', gaId, { path: '/' });
  };

  /** Load Platform TOU
   * @async
   * @description Sets Redux `state.entities.customerPlatformLink`. Checks
   *  if `platform_terms_of_use.pdf` exists in customer s3 bucket. 
   * * Defaults to: `urlsMap['PLATFORM_TERMS_OF_USE']`
   * @returns {void}
   */
  loadPlatformLink = async () => {
    if (!this.props.customerPlatformLink) {
      try {
        const platformTermsUrl = makeAssetLink(externalAssetNames.PLATFORM_TERMS_OF_USE);
        const customerTermsExists = await checkLink(platformTermsUrl);
        const customerPlatformLink = customerTermsExists 
          ? platformTermsUrl
          : urlsMap.get('PLATFORM_TERMS_OF_USE');
        this.props.setCustomerPlatformLink(customerPlatformLink);
      } catch (setPlatLinkErr) {
        console.error(setPlatLinkErr)
      }
    }
  }

  loadTemplate = marketplaceList => {
    // also happens in TopNav
    const mpId = getConfig('marketplaceId');
    const parentMarketplace = find(
      marketplaceList,
      mp => mp.marketplaceId == mpId
    );
    const cssConfig = get(parentMarketplace, 'cssConfig') || {};
    const template = makeTemplate(cssConfig);
    this.props.setTemplate(template); // puts it in the store
  };

  loadMarketplaceFeatures() {
    // we call marketplace-list api not the get-marketplace-features api
    // because we don't have an api key at this point
    const mpId = getConfig('marketplaceId');
    this.props
      .loadMarketplaces(mpId)
      .then(({ response }) => {
        if (response.wsStatus === 'Success') {
          const { marketplaceList } = response;
          this.loadTemplate(marketplaceList);
          this.loadGAID(marketplaceList);
        }
      })
      .catch(error => console.error(error));
  }

  // Opens the legacy ias-connect iframe (its own email + password screen).
  openIasConnectLogin = () => {
    const iasConnectRoot = getConfig('iasConnectRoot');
    const iasConnectUrl = encodeURI(
      `${iasConnectRoot}/account/login` +
        `?mpId=${getConfig('marketplaceId')}` +
        `&mobile=false` +
        `&redirect_uri=${window.location.origin}`
    );
    this.setState({ emailLoginModalOpen: false, iasConnectModalOpen: true, iasConnectUrl });
  };

  // Current AuthMode keeps the legacy ias-connect experience untouched; Sso goes straight to the
  // marketplace's own IdP; the mixed/forced modes open the native email screen, which decides between
  // SSO and ias-connect per the user's migration status (MP-2328).
  handleLogin = e => {
    if (e) e.preventDefault();
    const mode = getAuthMode();
    if (mode === AUTH_MODES.CURRENT) {
      this.openIasConnectLogin();
    } else if (mode === AUTH_MODES.SSO) {
      // No email prompt and no account-status check: the IdP authenticates everyone here, and the
      // migration table those steps consult is never populated for this marketplace. On success this
      // navigates away, so nothing after it runs.
      this.startSso();
    } else {
      this.setState({ emailLoginModalOpen: true });
    }
  };

  // Launch the IdP directly (popup when embedded, top-level redirect otherwise). Mirrors the error
  // handling in EmailLoginModal, since without that modal this is the only place it can surface.
  startSso = () => {
    startSsoLogin().catch(err => {
      if (err && err.message === 'popup_blocked') {
        toastr.error('Sign-in blocked', 'Please allow popups for this site to sign in.');
      } else if (err && err.message === 'popup_closed') {
        toastr.warning('Sign-in canceled', 'You closed the sign-in window before finishing.');
      } else {
        toastr.error('Sign-in failed', 'We couldn’t start sign-in. Please try again.');
      }
    });
  };

  // Opens the legacy ias-connect registration iframe (its own email + password + phone screens).
  openIasConnectRegistration = () => {
    const iasConnectRoot = getConfig('iasConnectRoot');
    const iasConnectUrl = encodeURI(
      `${iasConnectRoot}/register` +
        `?mpId=${getConfig('marketplaceId')}` +
        `&mobile=false` +
        `&redirect_uri=${window.location.origin}` +
        `&register=true`
    );
    this.setState({ registerModalOpen: false, iasConnectModalOpen: true, iasConnectUrl });
  };

  // A marketplace turns this off when it onboards users itself; it hides Register in every AuthMode. Only an
  // explicit false counts, so a fallback config from before the flag existed keeps registration on.
  isSelfRegistrationEnabled = () => getConfig('enableSelfRegistration') !== false;

  // Current AuthMode keeps registration in ias-connect, which owns its Keycloak identity flow. Sso sends
  // people to the IdP, which owns account creation there — the native screens would create a CIAM/Auth0
  // identity, and an Sso marketplace has none (marketplace-api's IsRegistrationAvailable refuses them for
  // the same reason); its users appear when the IdP's profile API is read on first sign-in. The mixed and
  // forced modes open the native registration screens, which register into SSO via marketplace-api.
  handleRegistration = e => {
    if (e) e.preventDefault();
    if (!this.isSelfRegistrationEnabled()) return;
    const mode = getAuthMode();
    if (mode === AUTH_MODES.CURRENT) {
      this.openIasConnectRegistration();
    //} else if (mode === AUTH_MODES.SSO) {
    //  this.startSso();
    } else {
      this.setState({ emailLoginModalOpen: false, registerModalOpen: true });
    }
  };

  renderSidebar() {
    const backgroundColor =
      getTemplate(this.props.template, 'signInButton.backgroundColor') ||
      '#6D6D6D';
    const color =
      getTemplate(this.props.template, 'signInButton.color') || '#FFF';
    const isQA = getConfig('apiRoot').includes('-qa');
    const isAppLinks = getConfig('appIOS') || getConfig('appAndroid');
    return (
      <div 
        className='marketplace-login__sidebar-container'
        // style={styles.sidebarContainer}
      >
        <img 
          alt="" 
          className='marketplace-login__sidebar-logo'
          src={makeAssetLink('logo.png')} 
          // style={styles.logo} 
        />
        <hr style={{ width: '100%', marginTop: 20, marginBottom: 20 }} />

        <button
          className="btn btn-block"
          style={{ backgroundColor, color, maxWidth: 200 }}
          onClick={this.handleLogin}
        >
          {isQA ? 'SIGN IN' : 'Sign In'}
        </button>
        {this.isSelfRegistrationEnabled() && (
          <button
            className="btn btn-block"
            style={{ backgroundColor, color, maxWidth: 200, marginTop: 20 }}
            onClick={this.handleRegistration}
          >
            {isQA ? 'REGISTER' : 'Register'}
          </button>
        )}
        {this.renderPublicEventLinks()}
        <hr style={{ width: '100%', marginTop: 20, marginBottom: 20 }} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ marginBottom: 5 }}>A Superior Marketplace</div>
          <div>{makeCompanyCopyright()}</div>
          <div>
            <a
              className='marketplace-login__link'
              href={this.props.customerPlatformLink}
              target="_blank"
              rel="noreferrer"
              >
              Platform Terms of Use
            </a>
            {` and `}
            <a
              className='marketplace-login__link'
              href={urlsMap.get('PLATFORM_PRIVACY_POLICY')}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
          </div>
        </div>

        {isAppLinks && (
          this.renderLinksToApps()
        )}
      </div>
    );
  }
  
  renderBackgroundImage() {
    const image = makeAssetLink('bg.jpg');
    
    return (
      <div
        className='marketplace-login__bg_container'
        style={{
          backgroundImage: `url(${image})`,
          // ...styles.backgroundContainer,
        }}
      />
    );
  }
  
  /**
   * (2025.11) - Added; Renders links to public runlists.  If no events listed, returns `null`.
   * @returns {React.Component | null}
   */
  renderPublicEventLinks() {
    const {publicEvents} = this.props
    const enablePublicEventsAtLogin = getConfig('enablePublicEventsAtLogin') === true;

    if (!enablePublicEventsAtLogin || !publicEvents?.publicEvents || !publicEvents?.publicEvents?.length) {
      return null;
    }

    return (
      <>
        <hr style={{ width: '100%', marginTop: 20, marginBottom: 20 }} />
        <div className='marketplace-login__events-list_container'>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <Icon name="bullhorn" size='large'/>
            <span className='marketplace-login__events-list_header-text'>View Runlists</span>
          </div>
          <ul className='marketplace-login__events-list_links'>
            {publicEvents.publicEvents.map((pubEvt, i) => {
              const {eventId, name, itemCount} = pubEvt;
              const itemCountText = `${name} (${itemCount} item${itemCount === 1 ? '' : 's'})`
              return (
                <li 
                  className='marketplace-login__events-list_link'
                  key={`${eventId}${i}`} 
                >
                  <a 
                    about='_blank' 
                    href={`/public-events/${eventId}`}
                    title={itemCountText}
                  >
                    {name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </>
    )
  }

  renderLinksToApps() {
    const iosAppUrl = getConfig('appIOS');
    const androidAppUrl = getConfig('appAndroid');
    
    return (
      <div 
        className='marketplace-login__apps-container'
      >
        <hr style={{ width: '100%', marginTop: 10, marginBottom: 5 }} />
        <h3 
          style={{
            margin: '0', 
            paddingBottom: '5px'
          }} 
          >
            {"Download our App!"}
        </h3>
        <div
          className='marketplace-login__apps-wrapper'
        >
          {iosAppUrl && 
            this.renderAppQRCode('iOS:  ', iosAppUrl)
          }
          {androidAppUrl && 
            this.renderAppQRCode('Android:  ', androidAppUrl)
          }
        </div>
      </div>
    )
  }

  /**
   * @param {string} heading
   * @param {string} appUrl
   * @returns {React.ReactNode}
   */
  renderAppQRCode(heading, appUrl){
    return (
      <div
        className='marketplace-login__apps-qr_wrapper'
      >
      <h4>{heading}</h4>
        <QRCode 
          style={{margin: '0 auto .5em'}} 
          value={appUrl} 
          size={80} 
        />
      </div>
    )
  }

  render() {
    const title = getConfig('name') ? `${getConfig('name')} | Login` : 'Login';

    return (
      <div>
        <IASConnectModal
          isOpen={this.state.iasConnectModalOpen}
          onClose={() => this.setState({ iasConnectModalOpen: false })}
          url={this.state.iasConnectUrl}
        />
        <EmailLoginModal
          isOpen={this.state.emailLoginModalOpen}
          onClose={() => this.setState({ emailLoginModalOpen: false })}
          onUseIasConnect={() => this.openIasConnectLogin()}
          showRegister={this.isSelfRegistrationEnabled()}
          onRegister={() => {
            this.setState({ emailLoginModalOpen: false });
            this.handleRegistration();
          }}
        />
        <RegisterModal
          isOpen={this.state.registerModalOpen}
          onClose={() => this.setState({ registerModalOpen: false })}
          onUnavailable={() => this.openIasConnectRegistration()}
        />
        <DocumentTitle title={title}>
          <div 
            // style={styles.container}
            className='marketplace-login__container'
          >
            <ReduxToastr
              timeOut={4000}
              newestOnTop={true}
              position="top-right"
            />
            {this.renderSidebar()}
            {this.renderBackgroundImage()}
          </div>
        </DocumentTitle>
      </div>
    );
  }
}

Login.propTypes = {
  loadMarketplaces: PropTypes.func.isRequired,
  loadPublicEvents: PropTypes.func.isRequired,
  setTemplate: PropTypes.func.isRequired,
  template: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { customerPlatformLink, publicEvents, template } = state.entities;
  return { customerPlatformLink, publicEvents, template };
};

export default connect(mapStateToProps, {
  loadMarketplaces,
  loadPublicEvents,
  setCustomerPlatformLink,
  setTemplate,
})(Login);